import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";

import DatagridTooltipProvider from "../ui/datagridTooltipProvider.js";
import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { Refresh } from "@mui/icons-material";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";

import * as CreatorActions from "../../state/redux/creatorSession";
import { mrtGetBodyRowSx } from "../../utils/helpers";
import Widget from "../ui/widget.js";
import WidgetSurveyContent from "../ui/widgetSurveyContent.js";
import WidgetTitle from "../ui/widgetTitle.js";

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const calculateColumns = theme => {
  return [
    {
      accessorKey: "botIdentifier",
      header: "Bot",
      size: 260,
      minSize: 260,
      maxSize: 260,
      grow: false,
      enableColumnFilter: true,
      enableSorting: false,
      filterVariant: "text",
      muiTableHeadCellProps: {
        align: "left",
        sx: {
          backgroundColor: alpha(theme.palette.commonground.contrastText, 0.12),
          color: theme.palette.commonground.contrastText,
          justifyContent: "start",
          whiteSpace: "nowrap",
          borderLeft: "none",
        },
      },
      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          verticalAlign: "top",
          borderLeft: "none",
        },
      },
      Cell: ({ cell }) => (
        <Typography
          title={cell.getValue()}
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "250px",
          }}
        >
          {cell.getValue()}
        </Typography>
      ),
    },
    {
      accessorKey: "newCount",
      header: "New",
      size: 90,
      minSize: 90,
      grow: true,
      enableColumnFilter: false,
      enableSorting: true,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
    },
    {
      accessorKey: "openCount",
      header: "Open",
      size: 90,
      minSize: 90,
      grow: true,
      enableColumnFilter: false,
      enableSorting: true,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
    },
    {
      accessorKey: "closedCount",
      header: "Closed",
      size: 100,
      minSize: 100,
      grow: true,
      enableColumnFilter: false,
      enableSorting: true,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
    },
    {
      accessorKey: "retestCount",
      header: "Retest",
      size: 100,
      minSize: 100,
      grow: true,
      enableColumnFilter: false,
      enableSorting: true,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
    },
    {
      accessorKey: "blockedCount",
      header: "Blocked",
      size: 110,
      minSize: 110,
      grow: true,
      enableColumnFilter: false,
      enableSorting: true,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
    },
    {
      accessorKey: "unknownCount",
      header: "Unknown",
      size: 120,
      minSize: 120,
      grow: true,
      enableColumnFilter: false,
      enableSorting: true,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
    },
    {
      accessorKey: "totalCount",
      header: "Total",
      size: 90,
      minSize: 90,
      grow: true,
      enableColumnFilter: false,
      enableSorting: true,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
          fontWeight: "bold",
        },
      },
    },
  ];
};

const OrganizationFeedbackStatusOverview = props => {
  const {
    organizationFeedbackStats = [],
    loadOrganizationFeedbackStatsRequest,
    widgetSize = "medium",
    minHeight = "0px",
  } = props;

  const theme = useTheme();

  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (isRefreshing) {
      setIsRefreshing(false);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationFeedbackStats]);

  const columns = useMemo(() => calculateColumns(theme), [theme]);

  const uniqueOrganizationFeedbackStats = useMemo(() => {
    const seenBots = new Set();

    return organizationFeedbackStats.filter(stat => {
      const botKey = stat.botId || stat.botIdentifier;

      if (seenBots.has(botKey)) {
        return false;
      }

      seenBots.add(botKey);
      return true;
    });
  }, [organizationFeedbackStats]);

  const handleRefreshClick = () => {
    setIsRefreshing(true);
    loadOrganizationFeedbackStatsRequest();

    setTimeout(() => {
      setIsRefreshing(false);
    }, 800);
  };

  const table = useMaterialReactTable({
    columns,
    data: uniqueOrganizationFeedbackStats,
    state: {
      isLoading: isRefreshing,
    },
    enableColumnActions: false,
    enableColumnFilters: true,
    enableColumnOrdering: false,
    enableColumnPinning: false,
    enableDensityToggle: false,
    enableFacetedValues: true,
    enableFullScreenToggle: true,
    enableGlobalFilter: false,
    enableGrouping: false,
    enablePagination: false,
    enableRowActions: false,
    enableRowSelection: false,
    enableSorting: true,
    enableTopToolbar: true,
    enableBottomToolbar: false,
    layoutMode: "grid",
    initialState: {
      density: "compact",
    },
    defaultColumn: {
      minSize: 20,
      maxSize: 9001,
      size: 50,
      grow: false,
    },
    muiTopToolbarProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.secondary.main,
        "& .MuiIconButton-root": {
          marginLeft: theme.spacing(1),
          padding: theme.spacing(0.9),
          backgroundColor: theme.palette.commonground.main,
          color: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "& .MuiSvgIcon-root": {
            fontSize: "1.35rem",
          },
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
        "& .MuiIconButton-root-active": {
          marginLeft: theme.spacing(1),
          padding: theme.spacing(0.9),
          color: theme.palette.secondary.contrastText,
          backgroundColor: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "& .MuiSvgIcon-root": {
            fontSize: "1.35rem",
          },
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        height: "100%",
      },
    },
    muiTableContainerProps: {
      sx: {
        minHeight: "424px",
        height: "100%",
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.main,
        border: `solid 0.5px ${theme.palette.divider}`,
        overflowX: "auto",
      },
    },
    muiTableProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.main,
        width: "100%",
      },
    },
    muiTableBodyRowProps: {
      sx: mrtGetBodyRowSx(),
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: alpha(theme.palette.commonground.contrastText, 0.12),
        color: theme.palette.commonground.contrastText,
        justifyContent: "center",
        whiteSpace: "nowrap",
      },
    },
    muiTableBodyCellProps: {
      sx: {
        borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
      },
    },
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        <Tooltip title="Reload feedback status stats" arrow>
          <span>
            <IconButton onClick={handleRefreshClick}>
              <Refresh
                sx={{
                  animation: isRefreshing ? `${spin} 0.8s linear` : "none",
                }}
              />
            </IconButton>
          </span>
        </Tooltip>

        {/* eslint-disable react/jsx-pascal-case */}
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        {/* eslint-enable react/jsx-pascal-case */}
      </Box>
    ),
    renderEmptyRowsFallback: () => (
      <Typography sx={{ padding: theme.spacing(2) }}>No feedback status records found.</Typography>
    ),
  });

  return (
    <Widget widgetSize={widgetSize} minHeight={minHeight}>
      <WidgetTitle
        title={"Feedback Overview"}
        text={`${uniqueOrganizationFeedbackStats.length} Botpress Bots`}
      />

      <WidgetSurveyContent>
        <Box sx={{ height: "100%", paddingTop: 0, paddingX: 1, paddingBottom: 1 }}>
          <DatagridTooltipProvider>
            <MaterialReactTable table={table} />
          </DatagridTooltipProvider>
        </Box>
      </WidgetSurveyContent>
    </Widget>
  );
};

const mapStateToProps = state => ({
  organizationFeedbackStats: state.creatorSession.organizationFeedbackStats,
});

const mapDispatchToProps = {
  loadOrganizationFeedbackStatsRequest: CreatorActions.loadOrganizationFeedbackStatsRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(OrganizationFeedbackStatusOverview);
